import './Newton.scss';
import { useEffect, useState } from 'react';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { Page } from '@Components/Page';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { isInternalSelector } from '@src/redux/user';
import { reportError } from '@src/utils/errorReporting';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { getNewtonToken, NEWTON_HOST } from './newton-api';
import { NewtonNotConfigured } from './NewtonNotConfigured';
import { NewtonProjectPicker } from './NewtonProjectPicker';
import { NewtonView, resolveNewtonView } from './newton-picker-utils';

const EMBED_SRC = `https://${NEWTON_HOST}/embed.js`;

interface NewtonMountOptions {
  container: string;
  getToken: () => Promise<string>;
  onError?: (e: unknown) => void;
  path?: string;
  title?: string;
  onAuthenticated?: () => void;
}

interface NewtonEmbedApi {
  mount: (options: NewtonMountOptions) => { iframe: HTMLIFrameElement; destroy: () => void };
}

declare global {
  interface Window {
    NewtonEmbed?: NewtonEmbedApi;
  }
}

// Inject the embed script once and resolve with the global it defines. Reuses an
// already-loaded global or an in-flight script tag so remounts don't double-inject.
const loadEmbedScript = (): Promise<NewtonEmbedApi> =>
  new Promise((resolve, reject) => {
    if (window.NewtonEmbed) {
      resolve(window.NewtonEmbed);
      return;
    }
    const onLoaded = () =>
      window.NewtonEmbed
        ? resolve(window.NewtonEmbed)
        : reject(new Error('Newton embed script loaded but NewtonEmbed is undefined'));
    const onFailed = () => reject(new Error('Failed to load the Newton embed script'));

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', onLoaded);
      existing.addEventListener('error', onFailed);
      return;
    }
    const script = document.createElement('script');
    script.src = EMBED_SRC;
    script.async = true;
    script.addEventListener('load', onLoaded);
    script.addEventListener('error', onFailed);
    document.head.appendChild(script);
  });

interface NewtonEmbedProps {
  projectSlug: string;
}

// Mounts the Newton dashboard routed to a specific project's home page.
const NewtonEmbed = ({ projectSlug }: NewtonEmbedProps) => {
  const setError = useSetError();

  useEffect(() => {
    let cancelled = false;
    let destroy: (() => void) | undefined;

    loadEmbedScript()
      .then(embed => {
        if (cancelled) return;
        ({ destroy } = embed.mount({
          container: '#newton',
          getToken: getNewtonToken,
          path: `/project/${projectSlug}/home`,
          // Runtime errors can fire transiently during Newton's silent token renewal, so
          // report them without interrupting the user with a modal.
          onError: e => {
            console.error('Newton embed error', e);
            reportError(e, { operation: 'Newton.embed' });
          }
        }));
      })
      .catch(e => {
        if (cancelled) return;
        setError({
          title: "Couldn't load Newton",
          message: `Failed to load Newton: ${e.message}`,
          reportError: e
        });
      });

    return () => {
      cancelled = true;
      destroy?.();
    };
  }, [projectSlug, setError]);

  return <div id='newton' className='newton-embed' />;
};

export const Newton = () => {
  const { cid, name, newton_project_slug } = useCompanyInfo();
  const isInternal = useAppSelector(isInternalSelector);

  // Local override so the page routes to the embed immediately after the picker saves,
  // without waiting for a company-info refetch to carry the persisted value back.
  const [justMappedSlug, setJustMappedSlug] = useState<string | null>(null);
  const projectSlug = justMappedSlug ?? newton_project_slug ?? null;
  const view = resolveNewtonView(projectSlug, isInternal);

  // Test companies (tinuititest, prospect, interview, etc.) render a real client's data
  // under the hood; show "Tinuiti" instead of leaking that client's name in the picker.
  const companyName = TEST_COMPANIES.includes(cid) ? 'Tinuiti' : name;

  return (
    <Page title='Audience & Planner' pageType='Audience & Planner'>
      {view === NewtonView.EMBED && <NewtonEmbed projectSlug={projectSlug as string} />}
      {view === NewtonView.PICKER && (
        <NewtonProjectPicker cid={cid} companyName={companyName} onMapped={setJustMappedSlug} />
      )}
      {view === NewtonView.NOT_CONFIGURED && <NewtonNotConfigured />}
    </Page>
  );
};
